import React, { useEffect, useState } from "react";
import ChevronDownIcon from "./icons/chevron-down";
import ChevronUpIcon from "./icons/chevron-up";
import PlusIcon from "./icons/plus";
import ThreeDotMenuIcon from "./icons/three-dot-menu";
import {
  Button,
  Collapse,
  Dropdown,
  Input,
  Menu,
  notification,
  Select,
  Space,
} from "antd";
import DeleteModal from "./delete-modal";
import TickOutlineIcon from "./icons/tick-outline-icon";
import CloseIcon from "./icons/close.jsx";
import { iconsMapping } from "./utils.js";
import { useUserGuide } from "./user-guide-context.jsx";
import styled from "styled-components";
import DeleteConfirmationModal from "./DeleteConfirmationModal.jsx";
import { getAllUserGuides, createUserGuide, updateUserGuide, deleteUserGuide } from "../../Services/userGuideServices";
import { transformUserGuidesToHierarchy } from "./helper/helper.js";
import { getImageURL } from "../../Services/deckleMatchServices";
import { uploadUserGuideImages } from "../../Services/uploadServices";

const { Panel } = Collapse;
const CustomSidebar = styled.div`
  .ant-collapse-content > .ant-collapse-content-box {
    padding-right: 0 !important;
  }
`;
export default function SidebarGuide({ edit, onEditAccessChange, preview, showToaster }) {
  const { state, handleUserGuideState } = useUserGuide();
  const [selectedKey, setSelectedKey] = useState(null);
  const [sections, setSections] = useState([]);
  const [tempTitles, setTempTitles] = useState({});
  const [loading, setLoading] = useState(true);
  // NOTE: openModal / openIconModal now store the entire item object (or null)
  const [openModal, setOpenModal] = useState(null);
  const [openIconModal, setOpenIconModal] = useState(null);
  const [renameItem, setRenameItem] = useState(null);
  const [selectedIcon, setSelectedIcon] = useState();
  const [activeKeys, setActiveKeys] = useState([]); // Track expanded sections
  const selectedItem = state.selectedItem;

  useEffect(() => {
    setRenameItem(null);
  }, [selectedKey]);

  // Helper function to get first page from a section
  const getFirstPage = (selectedSection) => {
    if (selectedSection?.page) return selectedSection;
    if (selectedSection?.subtabs?.length) {
      for (let i = 0; i < selectedSection?.subtabs?.length; i++) {
        const result = getFirstPage(selectedSection?.subtabs[i]);
        if (result) return result;
      }
    }
    return null;
  };

  // Helper function to calculate the depth of a node in the hierarchy
  const getNodeDepth = (node, sections) => {
    if (!node || !node.parent_id) {
      return 1; // Root level (tab)
    }
    
    // Find parent node and calculate depth recursively
    const findParent = (nodes, parentId) => {
      for (const n of nodes) {
        if (n.id === parentId) {
          return n;
        }
        if (n.subtabs && n.subtabs.length > 0) {
          const found = findParent(n.subtabs, parentId);
          if (found) return found;
        }
      }
      return null;
    };
    
    const parent = findParent(sections, node.parent_id);
    if (!parent) {
      return 1; // If parent not found, assume root level
    }
    
    return 1 + getNodeDepth(parent, sections);
  };

  useEffect(() => {
    // Fetch user guides from API
    const fetchUserGuides = async () => {
      try {
        setLoading(true);
        const response = await getAllUserGuides();
        
        if (response && response.data && Array.isArray(response.data)) {
          // Transform flat API response to hierarchical structure
          const hierarchicalSections = transformUserGuidesToHierarchy(response.data);
          setSections(hierarchicalSections);

          // Auto-select first page if available and no item is selected
          if (hierarchicalSections.length > 0 && !state.selectedItem) {
            const firstPage = getFirstPage(hierarchicalSections[0]);
            if (firstPage) {
              setSelectedKey(firstPage.id);
              handleUserGuideState({ selectedItem: firstPage });
            } else {
              // If no page found, select the first section
              const firstSection = hierarchicalSections[0];
              setSelectedKey(firstSection.id);
              handleUserGuideState({ selectedItem: firstSection });
            }
          }
        } else {
          // Fallback to default if API returns unexpected format
          setSections([]);
          if (showToaster) {
            showToaster("No user guides found", false);
          }
        }
        onEditAccessChange?.(true);
      } catch (error) {
        console.error("Error fetching user guides:", error);
        if (showToaster) {
          showToaster(error.message || "Failed to load user guides", true);
        }
        // Fallback to empty array on error
        setSections([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUserGuides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedItem) {
      setSelectedKey(parseInt(selectedItem?.id));
    }
  }, [selectedItem]);

  // Expand tab/subtab path so the open page is visible (not collapsed) initially
  useEffect(() => {
    if (selectedItem?.path) {
      const pathIds = selectedItem?.path.split("/")?.filter(Boolean);
      const ancestorIds = pathIds?.slice(0, -1)?.map((id) => String(id));
      setActiveKeys((prev) => {
        const combined = new Set([...ancestorIds, ...(Array.isArray(prev) ? prev : [])]);
        return Array.from(combined);
      });
    }
  }, [selectedItem?.id, selectedItem?.path]);

  // helper to find node by id in sections tree
  const findNodeById = (nodes, id) => {
    for (let node of nodes) {
      if (node.id === id) return node;
      if (node.subtabs?.length) {
        const found = findNodeById(node.subtabs, id);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper to check if any descendant of an item is selected
  const hasSelectedDescendant = (item, selectedId) => {
    if (!selectedId || !item) return false;
    const normalizedSelectedId = typeof selectedId === 'string' ? parseInt(selectedId, 10) : selectedId;
    const normalizedItemId = typeof item.id === 'string' ? parseInt(item.id, 10) : item.id;
    
    if (normalizedItemId === normalizedSelectedId) return true;
    
    if (item.subtabs && item.subtabs.length > 0) {
      return item.subtabs.some(subtab => hasSelectedDescendant(subtab, selectedId));
    }
    
    return false;
  };

  const handleDelete = async (item) => {
    if (!item?.id) {
      if (showToaster) {
        showToaster("Unable to delete item", true);
      }
      return;
    }

    try {
      // Call DELETE API
      await deleteUserGuide(item.id);

      // Remove from local state after successful API call
      const removeNode = (nodes, id) => {
        return nodes
          .map((n) => {
            if (n.id === id) return null;
            if (n.subtabs?.length) {
              return { ...n, subtabs: removeNode(n.subtabs, id).filter(Boolean) };
            }
            return n;
          })
          .filter(Boolean);
      };
      setSections((prev) => removeNode(prev, item.id));
      
      // Clear selectedItem if the deleted item is currently selected
      if (selectedItem?.id === item.id) {
        handleUserGuideState({ selectedItem: null });
        setSelectedKey(null);
      }
      
      setOpenModal(null);

      if (showToaster) {
        showToaster(item.page ? "Page deleted successfully" : "Item deleted successfully", false);
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      if (showToaster) {
        showToaster(error.message || "Failed to delete item", true);
      }
      // Don't update local state on error
    }
  };

  const handleUpdateIcon = async (item) => {
    if (!item?.id || !selectedIcon) {
      if (showToaster) {
        showToaster("Please select an icon", true);
      }
      return;
    }

    // Check if icon is the same as current icon
    if (item.icon === selectedIcon) {
      setOpenIconModal(null);
      setSelectedIcon(null);
      return; // No change needed
    }

    try {
      // Update icon via API
      await updateUserGuide(item.id, {
        icon: selectedIcon
      });

      // Update local state after successful API call
      const updateIcon = (nodes, id, icon) =>
        nodes.map((n) =>
          n.id === id
            ? { ...n, icon }
            : n.subtabs?.length
            ? { ...n, subtabs: updateIcon(n.subtabs, id, icon) }
            : n
        );
      setSections((prev) => updateIcon(prev, item.id, selectedIcon));
      setOpenIconModal(null);
      setSelectedIcon(null);

      if (showToaster) {
        showToaster("Icon updated successfully", false);
      }
    } catch (error) {
      console.error("Error updating icon:", error);
      if (showToaster) {
        showToaster(error.message || "Failed to update icon", true);
      }
    }
  };

  const customExpandIcon = ({ isActive }) => {
    return (
      <div>
        {isActive ? <ChevronUpIcon /> : <ChevronDownIcon color="#64748B" />}
      </div>
    );
  };

  const toggleEdit = (id, isEditMode, currentTitle) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === id ? { ...section, isEdit: isEditMode } : section
      )
    );
    setTempTitles((prev) => ({
      ...prev,
      [id]: currentTitle,
    }));
    setRenameItem(isEditMode ? id : null);
  };

  const handleRenameConfirm = async (item) => {
    const newTitle = tempTitles[item.id]?.trim();
    
    // Validate: check if title is provided and different
    if (!newTitle || newTitle === item.title) {
      // No change or empty title, just close edit mode
      toggleEdit(item.id, false, item.title);
      return;
    }

    try {
      // Update name via API
      await updateUserGuide(item.id, {
        name: newTitle
      });

      // Update local state after successful API call
      const renameInTree = (nodes) =>
        nodes.map((n) =>
          n.id === item.id
            ? { ...n, title: newTitle, name: newTitle }
            : n.subtabs?.length
            ? { ...n, subtabs: renameInTree(n.subtabs) }
            : n
        );
      setSections((prev) => renameInTree(prev));
      
      // Update selectedItem if it's the currently selected item
      if (selectedItem?.id === item.id) {
        handleUserGuideState({
          selectedItem: {
            ...selectedItem,
            title: newTitle,
            name: newTitle,
          },
        });
      }
      
      toggleEdit(item.id, false, newTitle);

      if (showToaster) {
        showToaster("Name updated successfully", false);
      }
    } catch (error) {
      console.error("Error renaming item:", error);
      if (showToaster) {
        showToaster(error.message || "Failed to update name", true);
      }
      // Don't update local state on error - keep original title
    }
  };

  const getSecureNumber = () => {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return (array[0] % 900) + 100;
  };

  const addSection = async () => {
    try {
      const title = `Untitled - ${getSecureNumber()}`;
      
      const response = await createUserGuide({
        name: title,
        path: null,
        publish_version: null, // Tabs don't have versions
        content: null,
        parent_id: null,
        type: "tab",
        icon: null
      });

      if (response && response.data) {
        const newTab = {
          id: response.data.id,
          uuid: response.data.uuid,
          title: response.data.name,
          page: false,
          path: `/${response.data.id}`,
          icon: null,
          subtabs: [],
          content: response.data.content,
          docUrl: response.data.path,
          publish_version: response.data.publish_version,
          created_at: response.data.created_at,
          updated_at: response.data.updated_at,
          parent_id: response.data.parent_id,
        };

        setSections((prev) => [...prev, newTab]);
        
        if (showToaster) {
          showToaster("Tab created successfully", false);
        }
      }
    } catch (error) {
      console.error("Error creating tab:", error);
      if (showToaster) {
        showToaster(error.message || "Failed to create tab", true);
      }
    }
  };

 const createSubTags = async (id, page) => {
  const parentNode = findNodeById(sections, id);
  if (!parentNode) {
    if (showToaster) {
      showToaster("Parent node not found", true);
    }
    return;
  }

  // Check depth: maximum 3 levels (tab -> subtab -> subtab)
  // If creating a subtab (not a page), check if parent is already at max depth
  if (!page) {
    const parentDepth = getNodeDepth(parentNode, sections);
    if (parentDepth >= 3) {
      if (showToaster) {
        showToaster("Maximum nesting depth reached. A tab can have a subtab, and that subtab can have a subtab, but no more.", true);
      }
      return;
    }
  }

    try {
      const title = `Untitled - ${getSecureNumber()}`;
      
      // Step 1: Create the page/subtab first
      // For pages, we'll create with draft status and update paths after S3 upload
      const response = await createUserGuide({
        name: title,
        path: null, // Will be set after S3 upload for pages
        publish_version: page ? "1.0.0" : null, // Pages have versions, tabs/subtabs don't
        content: page ? "" : null,
        parent_id: parentNode.id,
        type: page ? "page" : "tab",
        publish_status: page ? "draft" : undefined // Set publish_status to 'draft' for pages
      });

      if (response && response.data) {
        const pageId = response.data.id;
        let s3Path = null;
        let actualPath = null;

        // Step 2: For pages, upload empty HTML file to S3 first
        if (page) {
          try {
            const version = "1.0.0";
            const htmlFileName = `${pageId}.html`;
            const htmlBucketKey = `user-guide-documents/${pageId}/${version}/${htmlFileName}`;
            
            // Get presigned URL for HTML file upload
            const htmlPresignedUrl = await getImageURL({
              bucket_key: htmlBucketKey,
              operation: "upload"
            });

            // Create empty HTML content
            const emptyHtmlContent = "<p><br></p>";
            const htmlBlob = new Blob([emptyHtmlContent], { type: 'text/html' });
            const htmlFile = new File([htmlBlob], htmlFileName, { type: 'text/html' });

            // Upload empty HTML file to S3
            await uploadUserGuideImages(htmlPresignedUrl, htmlFile);

            // Set the correct S3 paths
            s3Path = `user-guide-documents/${pageId}/`;
            actualPath = `user-guide-documents/${pageId}/${version}/${htmlFileName}`;

            // Step 3: Update the page with the correct S3 paths and publish_status
            await updateUserGuide(pageId, {
              path: s3Path,
              actual_path: actualPath,
              publish_status: "draft"
            });
          } catch (s3Error) {
            console.error("Error uploading HTML to S3:", s3Error);
          // Continue even if S3 upload fails - the page is already created
          if (showToaster) {
            showToaster("Page created but S3 upload failed. You can publish later to save content.", false);
          }
          }
        }

      const newNode = {
        id: response.data.id,
        uuid: response.data.uuid,
        title: response.data.name,
        page: page,
        path: `${parentNode.path}/${response.data.id}`,
        subtabs: [],
        content: response.data.content,
        docUrl: s3Path || response.data.path, // Use updated S3 path if available
        publish_version: response.data.publish_version,
        created_at: response.data.created_at,
        updated_at: response.data.updated_at,
        parent_id: response.data.parent_id,
      };

      const insertChild = (nodes) =>
        nodes.map((n) =>
          n.id === id
            ? { ...n, subtabs: [...(n.subtabs || []), newNode] }
            : n.subtabs?.length
            ? { ...n, subtabs: insertChild(n.subtabs) }
            : n
        );

      setSections((prev) => insertChild(prev));

      if (showToaster) {
        showToaster(page ? "Page created successfully" : "Subtab created successfully", false);
      }

      // 👇 Auto-select and redirect to the new page if it's a page
      if (page) {
        // Ensure parent section is expanded so the new page is visible
        const parentId = String(id);
        setActiveKeys((prev) => {
          const keys = Array.isArray(prev) ? prev : [];
          if (!keys.includes(parentId)) {
            return [...keys, parentId];
          }
          return keys;
        });
        
        // Auto-select the new page
        setSelectedKey(newNode.id);
        handleUserGuideState({ selectedItem: newNode });
      }
    }
  } catch (error) {
    console.error("Error creating page/subtab:", error);
    if (showToaster) {
      showToaster(error.message || `Failed to create ${page ? "page" : "subtab"}`, true);
    }
  }
};


  const handleAction = (tree, targetId, action) => {
    return tree
      .map((item) => {
        if (item.id === targetId) {
          if (action === "rename") {
            toggleEdit(item?.id, true, item?.title);
            setRenameItem(item?.id);
            return { ...item, isEdit: true };
          }
        }

        if (item.subtabs?.length > 0) {
          return {
            ...item,
            subtabs: handleAction(item.subtabs, targetId, action).filter(
              Boolean
            ),
          };
        }

        return item;
      })
      .filter(Boolean);
  };

  const handleSectionClick = (selectedSection) => {
    if (preview) return;

    const firstPage = getFirstPage(selectedSection);
    if (firstPage) {
      setSelectedKey(firstPage?.id);
      handleUserGuideState({ selectedItem: firstPage });
    } else {
      setSelectedKey(selectedSection?.id);
      handleUserGuideState({ selectedItem: selectedSection });
    }
  };

  // keep same signature as original code: (id, action)
  const onMenuClick = async (id, action) => {
    if (action === "create-subtab" || action === "add-page") {
      const isPage = action === "add-page";
      await createSubTags(id, isPage);
    } else if (action == "add-icon") {
      const item = findNodeById(sections, id);
      setOpenIconModal(item);
    } else if (action === "delete") {
      const item = findNodeById(sections, id);
      handleDelete(item);
      // setOpenModal(item);
    } else {
      const updatedTree = handleAction(sections, id, action);
      setSections(updatedTree);
    }
  };

  const updateSectionById = (tree, id, updater) => {
    return tree.map((section) => {
      if (section.id === id) {
        return updater(section);
      } else if (section.subtabs?.length > 0) {
        return {
          ...section,
          subtabs: updateSectionById(section.subtabs, id, updater),
        };
      }
      return section;
    });
  };

  const renderDropdown = (id, page, subtabs, path = "", icon = null, parentId = null) => {
    // Find the node to check its depth
    const node = findNodeById(sections, id);
    const nodeDepth = node ? getNodeDepth(node, sections) : 1;
    // Hide "Add Subtab" if node is already at maximum depth (level 3)
    // Maximum nesting: Tab (level 1) -> Subtab (level 2) -> Subtab (level 3)
    const isMaxDepth = nodeDepth >= 3;
    
    return (
    <Dropdown
      trigger={["hover"]}
      placement="bottom"
      arrow
      overlay={
        <Menu
          onClick={({ key, domEvent }) => {
            // stop propagation from menu click to the Collapse header
            if (domEvent && domEvent.stopPropagation)
              domEvent.stopPropagation();
            onMenuClick(id, key);
          }}
        >
          <Menu.Item key="create-subtab" hidden={page || isMaxDepth}>
            Add Subtab
          </Menu.Item>
          <Menu.Item key="add-page" hidden={page}>
            Add Page
          </Menu.Item>
          <Menu.Item key="rename">Rename</Menu.Item>
          <Menu.Item
            key="add-icon"
            hidden={parentId !== null && parentId !== undefined}
          >
            {icon ? "Edit Icon" : "Add Icon"}
          </Menu.Item>
          <Menu.Item key="delete" hidden={subtabs?.length > 0}>
            Delete
          </Menu.Item>
        </Menu>
      }
    >
      <button
        type="button"
        className="ghost-btn"
        onClick={(e) => {
          // prevent Collapse header toggle when clicking the three-dot button
          if (e && e.stopPropagation) e.stopPropagation();
        }}
        onMouseDown={(e) => {
          if (e && e.stopPropagation) e.stopPropagation();
        }}
        style={{
          border: "none",
          background: "none",
          padding: "0",
          font: "inherit",
          color: "#605E5C",
          margin: "0",
          marginLeft: "4px",
          transform: "translateY(2.5px)",
        }}
      >
        <ThreeDotMenuIcon height="15" width="15" />
      </button>
    </Dropdown>
    );
  };

  const renderPanel = (items, parentActiveKeys = []) => (
    <CustomSidebar>
      <Collapse
        className={"sideBar"}
        ghost
        collapsible="icon"
        expandIconPosition="right"
        expandIcon={customExpandIcon}
        activeKey={parentActiveKeys.length > 0 ? parentActiveKeys : activeKeys}
        onChange={(keys) => {
          const rawKeys = Array.isArray(keys) ? keys : [keys];
          const filtered = rawKeys.filter((key) => {
            const node = findNodeById(sections, Number(key));
            return node && !node.page && node.subtabs?.length > 0;
          });
          setActiveKeys(filtered);
        }}
        style={{
          overflowX: "auto",
          height: "100%",
          width: "100%",
          overflowY: "hidden",
        }}
      >
        {items.map((item) => {
          const hasSubtabs = item.subtabs && item.subtabs.length > 0;
          // Normalize IDs for comparison (handle both string and number)
          const normalizedItemId = typeof item.id === 'string' ? parseInt(item.id, 10) : item.id;
          const normalizedSelectedKey = typeof selectedKey === 'string' ? parseInt(selectedKey, 10) : selectedKey;
          const isSelected = normalizedItemId === normalizedSelectedKey;
          
          // Check if this item is in the selected item's path hierarchy
          // This handles highlighting parent items when a child is selected
          let isInSelectedPath = false;
          if (state.selectedItem?.path) {
            // Split path and filter out empty strings, then check if this item's ID is in the path
            const pathIds = state.selectedItem.path
              .split('/')
              .filter(Boolean) // Remove empty strings
              .map(pathId => parseInt(pathId, 10));
            
            isInSelectedPath = pathIds.includes(normalizedItemId);
          }
          
          // Also check if the selected item's ID matches this item's ID
          const isSelectedItemMatch = state.selectedItem?.id 
            ? (typeof state.selectedItem.id === 'string' ? parseInt(state.selectedItem.id, 10) : state.selectedItem.id) === normalizedItemId
            : false;
          
          // Check if any descendant of this item is selected
          const hasDescendantSelected = hasSelectedDescendant(item, selectedKey);
          
          const selectedHir = isSelected || isInSelectedPath || isSelectedItemMatch || hasDescendantSelected;

          return (
            <Panel
              key={String(item.id)}
              className={item.page ? "pageBar" : "sideBar"}
              header={
                <div
                  className={item.page ? "pageBar" : "sideBar"}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderLeft: `4px solid ${
                      selectedHir ? "#ea655b" : "#FDF0EF"
                    }`,
                    paddingLeft: 8,
                    width: "100%",
                    //   fontFamily: "Space Grotesk",
                  }}
                  onClick={() => handleSectionClick(item)}
                >
                  <span>
                    {renameItem == item?.id ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                        // prevent header toggle when interacting with rename input/buttons
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <Input
                          value={tempTitles[item.id] ?? item.title}
                          onChange={(e) =>
                            setTempTitles((prev) => ({
                              ...prev,
                              [item.id]: e.target.value,
                            }))
                          }
                          onPressEnter={(e) => {
                            e.stopPropagation();
                            handleRenameConfirm(item);
                          }}
                          style={{ width: 170 }}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRenameConfirm(item);
                          }}
                        >
                          <TickOutlineIcon color="#189d57" size={23} />
                        </div>
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleEdit(item.id, false, item.title);
                            setSections((prev) =>
                              updateSectionById(prev, item.id, (section) => ({
                                ...section,
                                isEdit: false,
                              }))
                            );
                          }}
                        >
                          <CloseIcon color="#fe0909" size={12} />
                        </div>
                      </div>
                    ) : (
                      // <span
                      //   style={{
                      //     display: "flex",
                      //     alignItems: "center",
                      //     gap: 8,
                      //     fontWeight: isSelected ? "700" : "400",
                      //     color: selectedHir ? "#ea655b" : "#0F172A",
                      //     fontSize: '14px',
                      //     fontStyle:'var(--sf-pro-text)'
                      //   }}
                      // >
                      //   {item.title}
                      // </span>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          fontWeight: selectedHir ? "700" : "400",
                          color: selectedHir ? "#ea655b" : "#0F172A",
                          fontSize: "14px",
                          fontStyle: "var(--sf-pro-text)",
                        }}
                      >
                        {item.icon &&
                          (() => {
                            const matchedIcon = iconsMapping.find(
                              (ic) => ic.value === item.icon
                            );
                            return matchedIcon
                              ? selectedHir
                                ? matchedIcon.icon.active
                                : matchedIcon.icon.inactive
                              : null;
                          })()}

                        {item.title}
                      </span>
                    )}
                  </span>

                  {item.id != renameItem &&
                    !item?.page &&
                    edit &&
                    !preview &&
                    renderDropdown(
                      item.id,
                      item?.page,
                      item?.subtabs,
                      item.path,
                      item?.icon,
                      item?.parent_id
                    )}
                  {item.id != renameItem &&
                    item?.page &&
                    edit &&
                    !preview &&
                    renderDropdown(
                      item.id,
                      item?.page,
                      item?.subtabs,
                      item.path,
                      item?.icon,
                      item?.parent_id
                    )}
                </div>
              }
              showArrow={
                item.page ||
                item.isEdit ||
                !hasSubtabs ||
                renameItem == item?.id
                  ? false
                  : true
              }
              expandIcon={(panelProps) => {
                if (
                  item.page ||
                  item.isEdit ||
                  !hasSubtabs ||
                  renameItem == item?.id
                ) {
                  return (
                    <span
                      style={{ width: 16, height: 16, visibility: "hidden" }}
                    />
                  );
                }
                return customExpandIcon(panelProps);
              }}
            >
              {hasSubtabs ? renderPanel(item.subtabs) : null}
            </Panel>
          );
        })}
      </Collapse>
    </CustomSidebar>
  );

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Space
        direction="vertical"
        style={{ width: "100%", overflow: "auto", maxHeight: "95vh" }}
      >
        {edit && !preview && (
          <div
            style={{
              display: "flex",
              marginBottom: 8,
              justifyContent: "flex-end",
              marginRight: 8,
            }}
          >
            <Button
              className="btn"
              type="ghost"
              style={{
                backgroundColor: "#fff",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={addSection}
              icon={<PlusIcon color="#64748B" />}
            />
          </div>
        )}
        {loading ? (
          <div style={{ padding: "20px", textAlign: "center", color: "#64748B" }}>
            Loading user guides...
          </div>
        ) : sections?.length > 0 ? (
          renderPanel(sections)
        ) : (
          <div style={{ padding: "20px", textAlign: "center", color: "#64748B" }}>
            No user guides available
          </div>
        )}
      </Space>

      {/* GLOBAL MODALS — moved outside Panel headers to avoid Collapse click conflicts */}
      <DeleteModal
        isOpen={openModal}
        onClose={() => setOpenModal(null)}
        label={
          openModal?.page
            ? "Delete page?"
            : openModal?.path?.split("/").filter(Boolean).length === 1
            ? "Delete this tab"
            : "Delete this subtab"
        }
        text="Are you sure you want to delete? This action cannot be undone"
        loading={false}
        onConfirm={() => {
          if (openModal) handleDelete(openModal);
        }}
      />

      <DeleteConfirmationModal
        visible={openIconModal}
        cancelAction={() => {
          setSelectedIcon(null);
          setOpenIconModal(null);
        }}
        heading={"Select Icon for the tab"}
        action={"add_icon"}
        confirmAction={() => {
          if (openIconModal) handleUpdateIcon(openIconModal);
        }}
        text={
          <Select
            getPopupContainer={(triggerNode) => triggerNode.parentElement}
            defaultValue={openIconModal?.icon}
            options={iconsMapping}
            size="large"
            style={{ width: "260px", paddingTop: "10px" }}
            placeholder={"Select Icon"}
            onClick={(e) => e.stopPropagation()}
            onSelect={(e) => setSelectedIcon(e)}
          />
        }
      ></DeleteConfirmationModal>
    </div>
  );
}
